import { useQuery } from '@apollo/client';
import { useContext } from 'react';
import { client as productionClient, resourceDetailsQuery } from '../../../../../production/helpers/production';
import { client as resourceClient } from '../../../../../resource/resource';
import type { EntityName, EntitySecurity } from '../../../../auth/Security';
import { UserContext } from '../../../../auth/UserContext';
import { ENTITY_CONSTANTS } from '../../../../constants/entityConstants';
import { getNote, client as noteClient } from '../../../../notes/data/notesGraphqlApi';
import type { ShareableEntity } from '../../../../share';

type ShareEntityData = ShareableEntity & {
    identity?: { id?: string | null } | null;
    reference?: { id?: string | null } | null;
    security?: EntitySecurity | null;
};

type ShareQueryData = {
    resource?: ShareEntityData | null;
    note?: ShareEntityData | null;
};

export class SharePanelLoadError extends Error {}

export function useSharePanelData(entityName: EntityName, entityId: string) {
    const { activeOrganizationAccount } = useContext(UserContext);
    const isNote = entityName === ENTITY_CONSTANTS.note;
    const isSupported = isNote || entityName === ENTITY_CONSTANTS.resource;
    const client = isNote ? noteClient : productionClient;
    const { data, loading, error } = useQuery<ShareQueryData>(isNote ? getNote : resourceDetailsQuery, {
        client,
        variables: isNote ? { id: entityId } : { resourceId: entityId },
        context: { headers: { ownerId: activeOrganizationAccount } },
        fetchPolicy: 'network-only',
        skip: !isSupported || !entityId
    });
    const record = isNote ? data?.note : data?.resource;
    const recordId = record?.identity?.id ?? record?.reference?.id;
    const hasSharingData = typeof record?.restricted === 'boolean' && Boolean(record?.createdBy);
    const shareData = recordId === entityId && hasSharingData && !error ? record : undefined;
    const errorLoadingShare =
        error ||
        (!loading && isSupported && !shareData
            ? new SharePanelLoadError('Unable to load the requested sharing record and owner.')
            : undefined);
    const saveClient = isNote ? noteClient : resourceClient;
    const result = { shareData, isLoadingShare: loading, errorLoadingShare, isSupported, saveClient };
    return result;
}
